import React, { useState, useEffect } from 'react';
import {
    Users, Target, Zap, TrendingUp, AlertTriangle,
    CheckCircle2, Clock
} from 'lucide-react';
import WeeklyOpsReport from './WeeklyOpsReport';
import WorkspaceHealthDetail from './WorkspaceHealthDetail';
import {
    FileText, ChevronRight, ShieldCheck,
    ArrowUpRight, Heart, ArrowRight
} from 'lucide-react';
import { Badge } from './ui/badge';
import api from '../api/api';

export default function AdoptionStats() {
    const [funnel, setFunnel] = useState([]);
    const [friction, setFriction] = useState([]);
    const [retention, setRetention] = useState(null);
    const [cohorts, setCohorts] = useState([]);
    const [lifecycle, setLifecycle] = useState(null);
    const [commercial, setCommercial] = useState(null);
    const [risk, setRisk] = useState([]);
    const [summary, setSummary] = useState(null);
    const [healthStats, setHealthStats] = useState(null);
    const [selectedWS, setSelectedWS] = useState(null);
    const [wsHealth, setWsHealth] = useState(null);
    const [wsIntervention, setWsIntervention] = useState(null);
    const [showSummary, setShowSummary] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [funnelRes, frictionRes, retentionRes, cohortRes, commercialRes, riskRes, summaryRes, healthRes] = await Promise.all([
                api.get('/api/ops/adoption/funnel'),
                api.get('/api/ops/adoption/friction'),
                api.get('/api/ops/adoption/retention'),
                api.get('/api/ops/adoption/cohorts'),
                api.get('/api/ops/adoption/commercial'),
                api.get('/api/ops/adoption/churn-risk'),
                api.get('/api/ops/adoption/weekly-summary'),
                api.get('/api/ops/adoption/health')
            ]);
            setFunnel(funnelRes.data.data);
            setFriction(frictionRes.data.data);
            setRetention(retentionRes.data.data);
            setCohorts(cohortRes.data.data.cohorts);
            setLifecycle(cohortRes.data.data.lifecycle);
            setCommercial(commercialRes.data.data);
            setRisk(riskRes.data.data);
            setSummary(summaryRes.data.data);
            setHealthStats(healthRes.data.data);
        } catch (err) {
            console.error('Failed to fetch adoption stats', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchWSHealth = async (wsId) => {
        setSelectedWS(wsId);
        try {
            const [healthRes, interventionRes] = await Promise.all([
                api.get(`/api/ops/adoption/health/${wsId}`),
                api.get(`/api/ops/adoption/intervention/${wsId}`)
            ]);
            setWsHealth(healthRes.data.data);
            setWsIntervention(interventionRes.data.data);
        } catch (err) {
            console.error('Failed to fetch WS health/intervention', err);
        }
    };

    const handleIntervention = async (wsId, data) => {
        try {
            await api.post(`/api/ops/adoption/intervention/${wsId}`, data);
            // Refresh
            fetchWSHealth(wsId);
            fetchData();
        } catch (err) {
            console.error('Failed to submit intervention', err);
        }
    };

    const getFunnelProgress = (count, max) => {
        if (!max) return 0;
        return (count / max) * 100;
    };

    if (loading) return <div className="p-8 text-center text-muted-foreground">Analyzing adoption data...</div>;

    return (
        <div className="space-y-8 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Activation Funnel */}
                <div className="lg:col-span-1 space-y-6">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Activation Funnel (Global)
                    </h3>
                    
                    <div className="space-y-6">
                        {funnel.map((step, idx) => {
                            const prevCount = idx > 0 ? funnel[idx - 1].count : step.count;
                            const dropRate = idx > 0 ? (100 - (step.count / prevCount) * 100).toFixed(1) : 0;
                            
                            return (
                                <div key={step.label} className="relative">
                                    <div className="flex justify-between items-end mb-2">
                                        <div className="grid gap-0.5">
                                            <span className="text-xs font-bold text-slate-300">{step.label}</span>
                                            <span className="text-[10px] text-slate-500 font-medium uppercase tracking-tighter">Unique Users</span>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-lg font-black text-white">{step.count.toLocaleString()}</span>
                                            {idx > 0 && (
                                                <span className="ml-2 text-[10px] font-bold text-rose-500">
                                                    -{dropRate}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden border border-slate-700/50">
                                        <div 
                                            className={`h-full transition-all duration-1000 ${
                                                idx === 0 ? 'bg-indigo-500' : 
                                                idx === 1 ? 'bg-blue-500' : 
                                                idx === 2 ? 'bg-cyan-500' : 'bg-emerald-500'
                                            }`}
                                            style={{ width: `${getFunnelProgress(step.count, funnel[0].count)}%` }}
                                        />
                                    </div>
                                    {idx < funnel.length - 1 && (
                                        <div className="absolute -bottom-5 left-1/2 -translate-x-1/2">
                                            <ArrowRight className="w-3 h-3 text-slate-700 rotate-90" />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Recent Friction Signals */}
                <div className="lg:col-span-2 space-y-6">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        UX Friction & Blocked Actions
                    </h3>

                    <div className="bg-[#0a0a0c] border border-slate-800 rounded-xl overflow-hidden shadow-2xl">
                        <div className="max-h-[500px] overflow-y-auto custom-scrollbar">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-[#0d0d0f] text-slate-500 font-black uppercase tracking-tighter border-b border-slate-800">
                                    <tr>
                                        <th className="px-4 py-3">Time</th>
                                        <th className="px-4 py-3">User / Workspace</th>
                                        <th className="px-4 py-3">Issue</th>
                                        <th className="px-4 py-3">Context</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800/50">
                                    {friction.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-4 py-12 text-center text-slate-600 italic">No friction signals detected yet.</td>
                                        </tr>
                                    ) : (
                                        friction.map(sig => (
                                            <tr key={sig._id} className="hover:bg-slate-900/40 transition-colors group">
                                                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                                                    {new Date(sig.timestamp).toLocaleTimeString()}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="font-bold text-slate-300">{sig.userId?.name || 'Unknown'}</div>
                                                    <div className="text-[10px] text-slate-600 font-medium uppercase tracking-tighter">
                                                        {sig.workspaceId?.slug || 'Personal'}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[9px] font-black tracking-widest uppercase">
                                                        {sig.metadata?.type || 'BLOCKED'}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="text-slate-400 font-medium line-clamp-1 group-hover:line-clamp-none transition-all">
                                                        {sig.metadata?.message || 'Action restricted by policy or limit.'}
                                                    </div>
                                                    <div className="text-[10px] text-slate-600 font-mono mt-0.5">
                                                        {sig.metadata?.resource || 'GENERAL'}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Retention & Cohort Analytics (Sprint 22) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pt-8 border-t border-slate-800/50">
                {/* Retention Metrics */}
                <div className="lg:col-span-1 space-y-6">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                        Workspace Retention
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                        <div className="p-4 bg-slate-900/30 border border-slate-800 rounded-xl flex justify-between items-center">
                            <div>
                                <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Day 1</div>
                                <div className="text-2xl font-black text-white">{retention?.d1?.toFixed(1) || 0}%</div>
                            </div>
                            <Clock className="w-8 h-8 text-slate-800" />
                        </div>
                        <div className="p-4 bg-slate-900/30 border border-slate-800 rounded-xl flex justify-between items-center">
                            <div>
                                <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Day 7</div>
                                <div className="text-2xl font-black text-white">{retention?.d7?.toFixed(1) || 0}%</div>
                            </div>
                            <Clock className="w-8 h-8 text-slate-800" />
                        </div>
                        <div className="p-4 bg-slate-900/30 border border-slate-800 rounded-xl flex justify-between items-center">
                            <div>
                                <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Day 30</div>
                                <div className="text-2xl font-black text-white">{retention?.d30?.toFixed(1) || 0}%</div>
                            </div>
                            <Clock className="w-8 h-8 text-slate-800" />
                        </div>
                    </div>
                </div>

                {/* Cohort Analysis Table */}
                <div className="lg:col-span-2 space-y-6">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
                        <Users className="w-4 h-4 text-indigo-400" />
                        Account Cohort Activation
                    </h3>
                    <div className="bg-[#0a0a0c] border border-slate-800 rounded-xl overflow-hidden">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-[#0d0d0f] text-slate-500 font-black uppercase border-b border-slate-800">
                                <tr>
                                    <th className="px-4 py-3">Cohort (HTM)</th>
                                    <th className="px-4 py-3">Size</th>
                                    <th className="px-4 py-3">Activated (Search)</th>
                                    <th className="px-4 py-3">Power (Sequence)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/50 text-slate-300">
                                {cohorts.map(c => (
                                    <tr key={c.month} className="hover:bg-slate-900/40 transition-colors">
                                        <td className="px-4 py-3 font-bold">{c.month}</td>
                                        <td className="px-4 py-3">{c.size}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <span>{c.activated}</span>
                                                <Badge className="bg-emerald-500/10 text-emerald-500 text-[9px] py-0">
                                                    {((c.activated / c.size) * 100).toFixed(0)}%
                                                </Badge>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <span>{c.power}</span>
                                                <Badge className="bg-indigo-500/10 text-indigo-500 text-[9px] py-0">
                                                    {((c.power / c.size) * 100).toFixed(0)}%
                                                </Badge>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Commercial Activation & Churn Risk (Sprint 23) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-8 border-t border-slate-800/50">
                {/* Commercial Conversion Funnel */}
                <div className="space-y-6">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-amber-500" />
                        Commercial Conversion Path (7d)
                    </h3>
                    <div className="bg-[#0a0a0c] border border-slate-800 rounded-xl p-6 space-y-6">
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                                <div className="text-xl font-black text-white">{commercial?.frictionTotal || 0}</div>
                                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter">Limit Hits</div>
                            </div>
                            <div className="border-x border-slate-800">
                                <div className="text-xl font-black text-white">{commercial?.billingViews || 0}</div>
                                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter">Billing Views</div>
                            </div>
                            <div>
                                <div className="text-xl font-black text-emerald-400">{commercial?.upgradeIntents || 0}</div>
                                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter">Upgrade Initiated</div>
                            </div>
                        </div>

                        <div className="h-2 bg-slate-900 rounded-full overflow-hidden relative">
                             <div 
                                className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all duration-1000"
                                style={{ width: `${commercial?.conversionRatio || 0}%` }}
                             />
                        </div>
                        <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-slate-600">
                            <span>Friction Baseline</span>
                            <span className="text-emerald-500">{commercial?.conversionRatio || 0}% Conversion Velocity</span>
                        </div>
                    </div>
                </div>

                {/* Churn Risk Categories */}
                <div className="space-y-6">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-rose-500" />
                        Inactivity & Churn Risk Buckets
                    </h3>
                    <div className="bg-[#0a0a0c] border border-slate-800 rounded-xl divide-y divide-slate-800">
                        {risk.map(r => {
                            const wsData = healthStats?.workspaces?.find(hw => hw.workspaceId === r.label);
                            return (
                                <div key={r.label} className="p-4 flex justify-between items-center hover:bg-slate-900/20 transition-colors cursor-pointer group" onClick={() => fetchWSHealth(r.label)}>
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs font-bold text-slate-400">{r.label}</span>
                                        {wsData && (
                                            <Badge className={`text-[8px] px-1.5 py-0 font-black tracking-widest ${wsData.interventionStatus === 'NEW' ? 'bg-rose-500/10 text-rose-500' : 'bg-slate-800 text-slate-500'}`}>
                                                {wsData.interventionStatus}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`text-sm font-black ${r.count > 10 ? 'text-rose-500' : 'text-slate-300'}`}>{r.count}</span>
                                        <ChevronRight className="w-3 h-3 text-slate-700 group-hover:text-slate-300 transition-colors" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Success Health Distribution & Action Center (Sprint 24/25) */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 pt-8 border-t border-slate-800/50">
                <div className="lg:col-span-1 space-y-6">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
                        <Heart className="w-4 h-4 text-rose-400" />
                        Workspace Health Distribution
                    </h3>
                    <div className="bg-[#0a0a0c] border border-slate-800 rounded-xl p-6 flex items-center justify-center gap-8">
                        <div className="relative w-32 h-32">
                           <ShieldCheck className="absolute inset-0 m-auto w-12 h-12 text-slate-800/50" />
                           {/* Simplified Donut representation */}
                           <div className="absolute inset-0 rounded-full border-8 border-emerald-500/20" style={{ clipPath: `inset(0 0 0 0)` }} />
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="text-xs font-bold text-white">{healthStats?.distribution?.HEALTHY || 0} Healthy</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-amber-500" />
                                <span className="text-xs font-bold text-white">{healthStats?.distribution?.WATCH || 0} Watch</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-rose-500" />
                                <span className="text-xs font-bold text-white">{healthStats?.distribution?.AT_RISK || 0} At Risk</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-3 space-y-6">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
                        <ArrowUpRight className="w-4 h-4 text-indigo-400" />
                        Expansion & Success Action Center
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-indigo-500/5 border border-indigo-500/10 rounded-xl p-5 relative overflow-hidden group">
                            <div className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-3">Expansion Opportunities</div>
                            <div className="text-2xl font-black text-white flex items-center gap-3">
                                {healthStats?.expansionCount || 0}
                                <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 text-[9px] font-black">HIGH INTENT</Badge>
                            </div>
                            <p className="text-[10px] text-slate-500 mt-2">Accounts hitting plan caps with professional engagement velocity.</p>
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <ArrowUpRight className="w-3 h-3 text-indigo-400" />
                            </div>
                        </div>
                        <div className="bg-rose-500/5 border border-rose-500/10 rounded-xl p-5">
                            <div className="text-[10px] font-black text-rose-400 uppercase tracking-widest mb-3">Action Needed</div>
                            <div className="text-2xl font-black text-white flex items-center gap-3">
                                {healthStats?.workspaces?.filter(w => w.interventionStatus === 'NEW').length || 0}
                                <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30 text-[9px]">URGENT</Badge>
                            </div>
                            <p className="text-[10px] text-slate-500 mt-2">At-risk or stalled workspaces without initial review.</p>
                        </div>
                        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
                            <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Top Success Drivers</div>
                            <div className="space-y-2">
                                {healthStats?.topDrivers?.map(d => (
                                    <div key={d} className="text-[10px] font-medium text-slate-400 flex items-center gap-2">
                                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                        {d}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Product Pulse Summary (Updated for Sprint 23) */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 pt-4 mt-8 border-t border-slate-800/50">
               <div className="p-4 bg-slate-900/20 border border-slate-800/50 rounded-xl group cursor-pointer hover:bg-slate-800/30 transition-all" onClick={() => setShowSummary(true)}>
                    <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1 flex items-center gap-1">
                        Active WS
                    </div>
                    <div className="text-xl font-black text-indigo-400 flex items-baseline gap-2">
                        {summary?.activeWorkspaces.current || 0}
                        <span className="text-[10px] text-slate-500">WoW {summary?.activeWorkspaces.change}%</span>
                    </div>
               </div>
               <div className="p-4 bg-slate-900/20 border border-slate-800/50 rounded-xl">
                    <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">D1 Retention</div>
                    <div className="text-xl font-black text-white">{retention?.d1?.toFixed(1) || 0}%</div>
               </div>
               <div className="p-4 bg-slate-900/20 border border-slate-800/50 rounded-xl">
                    <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">Time to Search</div>
                    <div className="text-xl font-black text-emerald-400">{lifecycle?.avgMinutesToSearch || 0}m</div>
               </div>
               <div className="p-4 bg-slate-900/20 border border-slate-800/50 rounded-xl">
                    <div className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-1">Upgrade Intent</div>
                    <div className="text-xl font-black text-amber-500">{commercial?.upgradeIntents || 0}</div>
               </div>
               <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 border-dashed rounded-xl cursor-pointer hover:bg-indigo-500/20 transition-all flex flex-col justify-center items-center gap-1" onClick={() => setShowSummary(true)}>
                    <FileText className="w-4 h-4 text-indigo-400" />
                    <span className="text-[9px] font-black text-indigo-300 uppercase tracking-widest">Weekly Report</span>
               </div>
            </div>

            {showSummary && (
                <WeeklyOpsReport 
                    summary={summary} 
                    onClose={() => setShowSummary(false)} 
                />
            )}

            {selectedWS && (
                <WorkspaceHealthDetail 
                    workspaceId={selectedWS}
                    health={wsHealth}
                    intervention={wsIntervention}
                    onIntervention={(data) => handleIntervention(selectedWS, data)}
                    onClose={() => { setSelectedWS(null); setWsHealth(null); setWsIntervention(null); }}
                />
            )}
        </div>
    );
}
